import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { api, canManageSkus } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Check, Plus } from "lucide-react";

/**
 * SKU autocomplete with typo-warning, backed by the master SKU catalogue.
 *  - Fetches /api/bath-skus list on mount
 *  - Matches on sku AND alternate_sku
 *  - When a SKU is picked, fires onPick({ sku, alternate_sku, barcode, description })
 *    so callers can auto-fill multiple fields
 *  - Shows a small "Add to catalogue" CTA for admin/office when typing an unknown SKU
 *
 * Props:
 *   value: string
 *   onChange: (sku: string) => void
 *   onPick?: (entry: {sku, alternate_sku, barcode, description}) => void
 *   onRequestQuickAdd?: (typedSku: string) => void   // when user clicks "Add to catalogue"
 *   disabled?: boolean
 *   testId?: string
 */
export const SkuCombobox = ({
  value,
  onChange,
  onPick,
  onRequestQuickAdd,
  disabled,
  testId = "sku-combobox",
  refreshKey = 0,
}) => {
  const { user } = useAuth();
  const canCatalogue = canManageSkus(user);
  const [skus, setSkus] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/bath-records/skus")
      .then((r) => {
        if (!cancelled) setSkus(r.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const v = (value || "").trim();
  const vLower = v.toLowerCase();
  const matches = v
    ? skus
        .filter((s) => {
          const a = (s.sku || "").toLowerCase();
          const b = (s.alternate_sku || "").toLowerCase();
          return a.includes(vLower) || (b && b.includes(vLower));
        })
        .slice(0, 10)
    : skus.slice(0, 10);
  const exact = !!v && skus.some((s) =>
    (s.sku || "").toLowerCase() === vLower ||
    (s.alternate_sku || "").toLowerCase() === vLower
  );
  const showNewWarn = v.length >= 3 && !exact;

  const pick = (entry) => {
    onChange(entry.sku);
    if (onPick) onPick(entry);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value || ""}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        className="font-mono"
        data-testid={testId}
        autoComplete="off"
        placeholder="Start typing a SKU…"
      />
      {open && matches.length > 0 && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 bg-white border border-zinc-200 rounded-md shadow-lg max-h-72 overflow-auto"
          data-testid={`${testId}-dropdown`}
        >
          {matches.map((s) => (
            <button
              type="button"
              key={s.sku + "|" + (s.alternate_sku || "")}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 flex items-start gap-2"
              data-testid={`${testId}-option-${s.sku}`}
            >
              <span className="font-mono text-xs font-semibold text-zinc-950 shrink-0">{s.sku}</span>
              {s.alternate_sku && (
                <span className="font-mono text-[10px] text-zinc-500 shrink-0">⇄ {s.alternate_sku}</span>
              )}
              {s.barcode && (
                <span className="font-mono text-[10px] text-zinc-400 shrink-0">{s.barcode}</span>
              )}
              <span className="text-[11px] text-zinc-500 truncate">{s.description}</span>
            </button>
          ))}
        </div>
      )}
      {v && (
        <div className="mt-1.5 text-[11px] flex flex-wrap items-center gap-2">
          {exact ? (
            <span className="text-emerald-700 inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> Known SKU
            </span>
          ) : showNewWarn ? (
            <>
              <span className="text-amber-700 inline-flex items-center gap-1" data-testid={`${testId}-new-warning`}>
                <AlertTriangle className="h-3 w-3" />
                New SKU — please double-check spelling.
              </span>
              {canCatalogue && onRequestQuickAdd && (
                <button
                  type="button"
                  onClick={() => onRequestQuickAdd(v)}
                  className="text-amber-700 underline hover:text-amber-900 inline-flex items-center gap-1"
                  data-testid={`${testId}-add-to-catalogue`}
                >
                  <Plus className="h-3 w-3" /> Add this SKU to the catalogue
                </button>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};
